import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { CheckIcon, TrashIcon, PencilIcon, ClockIcon, SearchIcon } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

export function AssignmentTable({ 
  assignments,
  onEditAssignment,
  onCompleteAssignment,
  onRemoveAssignment,
  getStatusBadge
}) {
  const [assignmentSearchTerm, setAssignmentSearchTerm] = useState('');

  // Filter assignments based on search term
  const getFilteredAssignments = () => {
    if (!assignmentSearchTerm.trim()) {
      return assignments;
    }
    const searchLower = assignmentSearchTerm.toLowerCase().trim();
    return assignments.filter(assignment => 
      assignment.task?.title?.toLowerCase().includes(searchLower) ||
      assignment.work_step?.step_type?.toLowerCase().includes(searchLower) ||
      assignment.user?.full_name?.toLowerCase().includes(searchLower) ||
      assignment.status?.toLowerCase().includes(searchLower)
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Arbeitsschritt-Zuweisungen</h2>
        
        {/* Assignment Search */}
        <div className="flex justify-center">
          <div className="relative max-w-2xl w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder="Zuweisungen suchen (Aufgabe, Arbeitsschritt, Benutzer, Status)..."
              value={assignmentSearchTerm}
              onChange={(e) => setAssignmentSearchTerm(e.target.value)}
              className="pl-10 w-full"
            />
            {assignmentSearchTerm && (
              <button
                onClick={() => setAssignmentSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Assignments Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Aufgabe</TableHead>
                <TableHead>Arbeitsschritt</TableHead>
                <TableHead>Zugewiesen an</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Zuweisungsdatum</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getFilteredAssignments().map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell className="font-medium">
                    {assignment.task?.title || 'Unbekannte Aufgabe'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {assignment.work_step?.step_type || 'Unbekannter Schritt'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {assignment.user?.full_name || 'Unbekannter Benutzer'}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(assignment.status)}
                  </TableCell>
                  <TableCell>
                    {format(new Date(assignment.assigned_at), "PPP", { locale: de })}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditAssignment(assignment)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Bearbeiten
                      </Button>
                      {assignment.status !== 'Erledigt' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onCompleteAssignment(assignment.id, {
                              start_time: new Date().toISOString(),
                              end_time: new Date().toISOString(),
                              actual_quantity: 0,
                              work_notes: "Von Admin als erledigt markiert",
                              completion_notes: ""
                            })}
                            className="text-green-600 hover:text-green-700"
                          >
                            <CheckIcon className="h-4 w-4 mr-1" />
                            Erledigt
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (window.confirm(`Zuweisung von ${assignment.user?.full_name} wirklich entfernen?`)) {
                                onRemoveAssignment(assignment.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            Entfernen
                          </Button>
                        </>
                      )}
                      {assignment.status === 'Erledigt' && assignment.start_time && assignment.end_time && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-blue-600 hover:text-blue-700">
                              <ClockIcon className="h-4 w-4 mr-1" />
                              Details
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Arbeitsschritt-Details</DialogTitle>
                              <DialogDescription>
                                Erfasste Arbeitszeiten und Ergebnisse von {assignment.user?.full_name}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-sm font-medium text-gray-600">Arbeitszeit</Label>
                                  <p className="text-sm">
                                    {format(new Date(assignment.start_time), "HH:mm")} - {format(new Date(assignment.end_time), "HH:mm")}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-sm font-medium text-gray-600">Dauer</Label>
                                  <p className="text-sm font-semibold text-blue-600">
                                    {Math.round((new Date(assignment.end_time) - new Date(assignment.start_time)) / (1000 * 60))} Minuten
                                  </p>
                                </div>
                              </div>
                              <div>
                                <Label className="text-sm font-medium text-gray-600">Ist-Stückzahl</Label>
                                <p className="text-sm">{assignment.actual_quantity || 'Nicht angegeben'}</p>
                              </div>
                              {assignment.work_notes && (
                                <div>
                                  <Label className="text-sm font-medium text-gray-600">Arbeitsnotizen</Label>
                                  <p className="text-sm">{assignment.work_notes}</p>
                                </div>
                              )}
                              {assignment.completion_notes && (
                                <div>
                                  <Label className="text-sm font-medium text-gray-600">Abschluss-Notizen</Label>
                                  <p className="text-sm">{assignment.completion_notes}</p>
                                </div>
                              )}
                              <div>
                                <Label className="text-sm font-medium text-gray-600">Abgeschlossen am</Label>
                                <p className="text-sm">{format(new Date(assignment.completed_at), "PPP HH:mm", { locale: de })}</p>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default AssignmentTable;
